import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import {
  getEvent,
  getRegistrations,
  exportCf9,
  exportIdx14,
  exportDataInputForm,
} from '../services/api';
import { getErrorMessage } from '../utils/errors';
import type { RacingEvent, Registration, RegistrationClassEntry } from '../types';
import { PageHeader, Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@racing/ui';

/**
 * Every export is per class entry, not per registration — a driver entered in
 * two classes produces two lines. This is the fallback when a registration has
 * no explicit entries, so the counts and previews agree with the server.
 */
const entriesOf = (registration: Registration): Partial<RegistrationClassEntry>[] =>
  registration.class_entries && registration.class_entries.length > 0
    ? registration.class_entries
    : [
        {
          class_code: registration.class_code,
          car_number: registration.car_number,
          category: registration.category,
        } as Partial<RegistrationClassEntry>,
      ];

const ExportCard = ({
  title,
  description,
  preview,
  buttonLabel,
  onDownload,
}: {
  title: string;
  description: string;
  preview: ReactNode;
  buttonLabel: string;
  onDownload: () => void;
}) => (
  <Card className="flex min-w-[18rem] flex-1 flex-col">
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-3">
      <div>
        <p className="mb-1.5 font-sans text-xs font-semibold uppercase tracking-[0.1em] text-ink-faint">
          Preview — first 3 entries
        </p>
        {/* Fixed-format output: it must not wrap, so it scrolls in place. */}
        <pre className="max-h-[9rem] overflow-auto rounded-md bg-surface-sunk p-2 font-mono text-xs leading-relaxed text-ink-soft">
          {preview}
        </pre>
      </div>
      <div className="mt-auto">
        <Button onClick={onDownload}>
          <Download /> {buttonLabel}
        </Button>
      </div>
    </CardContent>
  </Card>
);

const ExportPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const eventId = id ? Number(id) : 0;

  const [event, setEvent] = useState<RacingEvent | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [eventData, regData] = await Promise.all([
        getEvent(eventId),
        // pageSize 5 is only for the preview; the export itself is server-side
        // and covers every registration.
        getRegistrations(eventId, { page: 1, pageSize: 5 }),
      ]);
      setEvent(eventData);
      setRegistrations(regData.data);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!id) return <Navigate to="/events" replace />;

  const download = async (fetcher: () => Promise<Blob>, filename: string) => {
    try {
      const blob = await fetcher();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  const lineCount = registrations.reduce((sum, reg) => sum + entriesOf(reg).length, 0);

  /** Sequence runs across entries, not registrations — hence the shared counter. */
  const cf9Preview = (() => {
    let seq = 0;
    return registrations.slice(0, 3).flatMap((reg) =>
      entriesOf(reg).map((entry, index) => {
        seq += 1;
        const seqId = `B${String(seq).padStart(3, '0')}`;
        return (
          <div key={`${reg.registration_id}-${index}`}>
            {seqId},{entry.class_code || '9'},,{reg.first_name} {reg.last_name},,
            {reg.street || ''},{reg.city || ''} {reg.state || ''},{reg.zip || ''},…
          </div>
        );
      })
    );
  })();

  const idx14Preview = (() => {
    let seq = 0;
    return registrations.slice(0, 3).flatMap((reg) =>
      entriesOf(reg).map((entry, index) => {
        seq += 1;
        return (
          <div key={`${reg.registration_id}-${index}`}>
            {seq},{entry.class_code || ''},0,0,{reg.registration_id},{reg.first_name}{' '}
            {reg.last_name},,
          </div>
        );
      })
    );
  })();

  const xlsxPreview = registrations.slice(0, 3).flatMap((reg) =>
    entriesOf(reg).map((entry, index) => (
      <div key={`${reg.registration_id}-${index}`}>
        {entry.car_number || '—'} | {reg.first_name} {reg.last_name} |{' '}
        {entry.class_code || '—'} | {entry.category || '—'}
      </div>
    ))
  );

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2 self-start"
        onClick={() => navigate(`/events/${eventId}`)}
      >
        <ArrowLeft /> Back to event
      </Button>

      <PageHeader
        title="Compulink Export"
        subtitle={
          event?.event_name
            ? `${event.event_name} — ${registrations.length} registration${registrations.length === 1 ? '' : 's'} previewed, ${lineCount} export line${lineCount === 1 ? '' : 's'}`
            : undefined
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {registrations.length === 0 ? (
        <Alert variant="info">No registrations to export. Add registrations first.</Alert>
      ) : (
        <div className="flex flex-wrap gap-4">
          <ExportCard
            title="Participant File (cf9)"
            description="Compulink participant import. Each entry carries sequence ID, class code, driver name and address."
            preview={cf9Preview}
            buttonLabel="Download cf9"
            onDownload={() =>
              download(() => exportCf9(eventId), `event_${eventId}_participants.cf9`)
            }
          />
          <ExportCard
            title="Class Index File (idx14)"
            description="Compulink class index import. CSV of sequence numbers, class codes and driver name pairings."
            preview={idx14Preview}
            buttonLabel="Download idx14"
            onDownload={() =>
              download(() => exportIdx14(eventId), `event_${eventId}_classindex.idx14`)
            }
          />
          <ExportCard
            title="Data Input Form (xlsx)"
            description="Excel workbook for Compulink data input: driver info, vehicle details, membership and announcement lines."
            preview={xlsxPreview}
            buttonLabel="Download xlsx"
            onDownload={() =>
              download(
                () => exportDataInputForm(eventId),
                `event_${eventId}_data_input_form.xlsx`
              )
            }
          />
        </div>
      )}
    </div>
  );
};

export default ExportPage;
